// LN Pro · the Instagram half of the Content tab's actions lane.
//
// The container: it owns the read, the filter and the recording call; ActionDesk
// owns the drawing. The producer is instagramActionsService (PKT-0633's seam),
// so the desk shows what the Workbench actually ranked rather than a placeholder.
//
// 🔴 THE EMPTY STATE IS THE FEATURE. This surface can be empty for two
// completely different reasons and a desk that renders them identically teaches
// the operator the wrong thing:
//
//   "nothing scored high enough today"   → the system worked; come back later
//   "the roster is unverified"           → the system CANNOT rank yet, because
//                                          every influencer→LocateNow edge is
//                                          UNKNOWN until PKT-0632 completes
//
// The sentence comes from emptyStateReason(), which takes the QUEUE rather than
// a boolean precisely so that distinction survives. An empty desk that looks
// calm is how a broken feed goes unnoticed for a week.
//
// It also carries the lesson from the Reddit desk (PR #503): a read that fails
// AFTER a successful one must say so. Rows that stopped updating look exactly
// like rows that did not change.

import React from "react";
import { Alert, Button } from "@mui/material";
import ActionDesk from "./ActionDesk";
import {
  emptyStateReason,
  confirmInstagramReceipt,
  InstagramReceiptUnconfirmedError,
  instagramActionsService,
  isInstagramActionsWakingError,
  type InstagramActionQueue,
} from "../../services/instagramActionsService";
import type { ActionVerb } from "../../services/actionsContract";

export interface InstagramActionDeskProps {
  /** Injected by tests. Defaults to the real producer. */
  service?: Pick<typeof instagramActionsService, "list" | "action">;
}

export default function InstagramActionDesk({
  service = instagramActionsService,
}: InstagramActionDeskProps) {
  // Workbench serves the current eligible pending queue, not receipt history.
  const filter = "needs_action" as const;
  const keepPendingFilter = () => {};
  const [queue, setQueue] = React.useState<InstagramActionQueue | null>(null);
  const [loadState, setLoadState] = React.useState<"loading" | "ready" | "waking" | "error">("loading");
  const [blockedActionIds, setBlockedActionIds] = React.useState<Set<string>>(new Set());
  const [receiptNotice, setReceiptNotice] = React.useState<string | null>(null);
  const [failure, setFailure] = React.useState<string | null>(null);

  const contextEpoch = React.useRef(0);
  const readEpoch = React.useRef(0);
  const active = React.useRef(false);
  React.useLayoutEffect(() => {
    active.current = true;
    contextEpoch.current += 1;
    setQueue(null);
    setBlockedActionIds(new Set());
    setReceiptNotice(null);
    setFailure(null);
    setLoadState("loading");
    return () => { active.current = false; contextEpoch.current += 1; readEpoch.current += 1; };
  }, [service]);

  const load = React.useCallback(async () => {
    if (!active.current) return;
    const context = contextEpoch.current;
    const request = ++readEpoch.current;
    const current = () => active.current && context === contextEpoch.current && request === readEpoch.current;
    setLoadState("loading");
    try {
      const next = await service.list("needs_action");
      if (!current()) return;
      setQueue(next);
      setLoadState("ready");
      setFailure(null);
    } catch (error) {
      if (!current()) return;
      setLoadState(isInstagramActionsWakingError(error) ? "waking" : "error");
      setFailure(error instanceof Error ? error.message : String(error));
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Nothing has ever loaded: the whole desk is the failure.
  if (!queue && loadState === "waking") {
    return (
      <Alert severity="info" action={<Button onClick={() => void load()}>Try again</Button>} data-testid="instagram-waking">
        The Instagram actions desk is waking up. A cold start can take about a minute.
      </Alert>
    );
  }
  if (!queue && loadState === "error") {
    return <ActionDesk cards={[]} counts={{}} filter={filter} onFilter={keepPendingFilter} onAct={async () => {}} error={failure ?? "unavailable"} />;
  }
  if (!queue) {
    return <ActionDesk cards={[]} counts={{}} filter={filter} onFilter={keepPendingFilter} onAct={async () => {}} loading />;
  }

  return (
    <>
      {receiptNotice && <Alert severity="info" data-testid="instagram-receipt-notice">{receiptNotice}</Alert>}
      {loadState !== "ready" && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={<Button disabled={loadState === "loading"} onClick={() => void load()}>Try again</Button>}
          data-testid="instagram-stale"
        >
          {loadState === "loading"
            ? "Refreshing these rows. Receipt actions are paused."
            : "These rows could not be refreshed — you are looking at the last successful read. Receipt actions are paused until a refresh succeeds."}
        </Alert>
      )}
      <ActionDesk
        cards={queue.items}
        counts={queue.counts}
        filter={filter}
        filterOptions={["needs_action"]}
        onFilter={keepPendingFilter}
        emptyNote={emptyStateReason(queue) ?? undefined}
        blockedActionIds={loadState === "ready"
          ? blockedActionIds
          : new Set(queue.items.map((card) => card.id))}
        onAct={async (card, verb: ActionVerb, _text, dismissReason) => {
          if (!active.current || loadState !== "ready" || blockedActionIds.has(card.id)) return;
          const context = contextEpoch.current;
          const current = () => active.current && context === contextEpoch.current;
          // There is no executor here. Every verb records what a HUMAN already
          // did, which is why `copied` cannot reach this branch: Instagram
          // offers no Copy verb, because there is no text to copy.
          if (verb === "copied") throw new Error("Instagram actions carry no text to copy.");
          try {
            const result = await service.action(card.id, verb === "dismissed"
              ? { action: verb, dismiss_reason: dismissReason }
              : { action: verb });
            if (!current()) return;
            confirmInstagramReceipt(result, card.id, verb);
            setBlockedActionIds((previous) => new Set(previous).add(card.id));
            setReceiptNotice("Your human-action receipt was recorded.");
            await load();
          } catch (error) {
            if (!current()) return;
            if (error instanceof InstagramReceiptUnconfirmedError) {
              setBlockedActionIds((previous) => new Set(previous).add(card.id));
              setReceiptNotice(error.message);
            }
            throw error;
          }
        }}
      />
    </>
  );
}
